import React, { useEffect, useState } from "react";
import {
  FiEye,
  FiX,
  FiSave,
  FiChevronRight,
  FiPlus,
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import useClients from "../../../Services/ClientServices/ClientConfig/useClientConfig";

const THEME_BLUE_BG = "bg-[#F8FBFF]";
const BORDER_SMART = "border-gray-300";

const DEFAULT_PAGE = {
  page_id: "",
  title: "",
  sequence: "",
  content: "",
  status: "active",
};

function PagesSection({ clientId }) {
  const { fetchPages, fetchPageById, savePage } = useClients();

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(DEFAULT_PAGE);
  const [saving, setSaving] = useState(false);

  const loadPages = async () => {
    setLoading(true);
    const data = await fetchPages(clientId);
    setPages(data);
    setLoading(false);
  };

   useEffect(() => {
    if (clientId) loadPages();
  }, [clientId]);


  /* ---------- OPEN CREATE PAGE MODAL ---------- */
  const openCreatePageModal = () => {
    setForm(DEFAULT_PAGE);
    setOpenModal(true);
  };

 const openPageModal = async (page_id) => {
    const toastId = toast.loading("Fetching page content...");
    setOpenModal(true);

    const page = await fetchPageById({
      userid: clientId,
      page_id,
    });

    if (page) {
      setForm({
        page_id,
        title: page.title || "",
        sequence: page.sequence || "",
        content: page.content || "",
        status: page.status || "active",
      });
    }

    toast.dismiss(toastId);
  };

  const closeModal = () => {
    setOpenModal(false);
    setForm(DEFAULT_PAGE);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Page title is required");
      return;
    }

    setSaving(true);

    const success = await savePage({
      userid: clientId,
      page_id: form.page_id,
      title: form.title,
      sequence: form.sequence,
      content: form.content,
      status: form.status,
    });

    if (success) {
      closeModal();
      loadPages();
    }

    setSaving(false);
  };

  return (
    <div className={`rounded-2xl bg-white p-7 shadow-sm border ${BORDER_SMART}`}>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-gray-400">
            Site Architecture
          </div>
          <p className="text-xs text-gray-500 font-semibold mt-1">
            Manage static pages, legal policies, and ordering
          </p>
        </div>

        {pages.length > 0 && (
          <button
            onClick={openCreatePageModal}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#01C37D] text-white text-[11px] font-extrabold uppercase tracking-widest shadow hover:bg-[#00ab6d] transition-all"
          >
            <FiPlus /> Add Page
          </button>
        )}
      </div>

      {/* CONTENT */}
      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#01C37D]/20 border-t-[#01C37D]" />
        </div>
      ) : pages.length === 0 ? (
        <div className="py-14 text-center border border-dashed border-gray-300 rounded-2xl flex flex-col items-center gap-4">
          <p className="text-gray-400 text-sm font-medium">
            No web pages found for this client.
          </p>
          <button
            onClick={openCreatePageModal}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#01C37D] text-white text-xs font-extrabold uppercase tracking-widest shadow hover:bg-[#00ab6d] transition-all"
          >
            <FiPlus /> Add First Page
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {pages.map((page) => (
            <div
              key={page.page_id}
              className="group flex items-center justify-between p-4 rounded-2xl border border-gray-300 hover:border-[#01C37D]/30 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4">
                <div className={`h-10 w-10 rounded-xl ${THEME_BLUE_BG} border flex items-center justify-center`}>
                  <span className="text-xs font-bold">{page.sequence || 0}</span>
                </div>
                <div>
                  <h4 className="text-[13px] font-bold uppercase">{page.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`h-2 w-2 rounded-full ${page.status === "active" ? "bg-[#01C37D]" : "bg-gray-300"}`} />
                    <span className="text-[10px] font-bold uppercase text-gray-400">
                      {page.status}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openPageModal(page.page_id)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 border text-[11px] font-extrabold uppercase hover:bg-[#01C37D] hover:text-white transition-all"
              >
                <FiEye /> Edit <FiChevronRight />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {openModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden">
            <div className="px-8 py-6 border-b flex justify-between items-center bg-[#F8FBFF]">
              <h3 className="text-lg font-bold">
                {form.page_id ? "Edit Page" : "Create Page"}
              </h3>
              <button onClick={closeModal}>
                <FiX size={20} />
              </button>
            </div>

            <div className="p-8 space-y-5">
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Page Title"
                className="w-full px-4 py-3 border rounded-xl"
              />
              <input
                name="sequence"
                type="number"
                value={form.sequence}
                onChange={handleChange}
                placeholder="Sequence"
                className="w-full px-4 py-3 border rounded-xl"
              />
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full px-4 py-3 border rounded-xl"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <textarea
                name="content"
                value={form.content}
                onChange={handleChange}
                rows={8}
                placeholder="Page content (HTML supported)"
                className="w-full px-4 py-3 border rounded-xl"
              />
            </div>

            <div className="px-8 py-5 border-t flex justify-end gap-3 bg-gray-50">
              <button onClick={closeModal} className="px-6 py-2 rounded-xl text-sm">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-8 py-2 rounded-xl bg-[#01C37D] text-white font-bold disabled:opacity-50"
              >
                <FiSave /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PagesSection;
